// Shows the original PDF a RAG answer cited, with the specific chunk highlighted.
// Every ingested document carries character offsets (chunk_start/chunk_end, set by
// rag/embedding/dataprocessing.py) which sourceModalUtils' buildPageHighlightIndex maps
// to a (page, in-page-range) so the exact matching text can be <mark>-highlighted via
// react-pdf's customTextRenderer.
import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Document, Page } from "react-pdf"
import {
    buildPageHighlightIndex,
    escapeHtml,
    getSourceKey,
    isFiniteNumber,
    resolveBestPageForOffsets
} from "./sourceModalUtils"
import type {
    MessageSource,
    PageHighlightIndex
} from "./sourceModalUtils"

import "react-pdf/dist/Page/TextLayer.css"
import "react-pdf/dist/Page/AnnotationLayer.css"

type MessageSourceWithOffsets = MessageSource & {
    chunk_start?: number | null
    chunk_end?: number | null
}

type SourceDocumentModalProps = {
    isOpen: boolean
    isLoading: boolean
    error: string | null
    documentUrl: string | null
    documentTitle: string
    sources: MessageSource[]
    activeSourceIndex: number
    onActiveSourceIndexChange: (index: number) => void
    onClose: () => void
}

function SourceDocumentModal({
    isOpen,
    isLoading,
    error,
    documentUrl,
    documentTitle,
    sources,
    activeSourceIndex,
    onActiveSourceIndexChange,
    onClose
}: SourceDocumentModalProps) {
    const [pageCount, setPageCount] = useState(0)
    const [activePage, setActivePage] = useState(1)
    const [highlightIndex, setHighlightIndex] = useState<PageHighlightIndex[]>([])

    const activeSource = sources[activeSourceIndex] ?? null

    useEffect(() => {
        if (!isOpen) {
            setPageCount(0)
            setActivePage(1)
            setHighlightIndex([])
            return
        }

        if (documentUrl === null) {
            return
        }

        let isCancelled = false

        const prepare = async () => {
            try {
                const result = await buildPageHighlightIndex(documentUrl)
                if (isCancelled) return
                setPageCount(result.pageCount)
                setHighlightIndex(result.indexes)
            } catch (buildError) {
                console.error("Error preparing PDF highlight index:", buildError)
            }
        }

        prepare()

        return () => {
            isCancelled = true
        }
    }, [documentUrl, isOpen])

    useEffect(() => {
        const sourceWithOffsets = activeSource as MessageSourceWithOffsets | null
        if (
            sourceWithOffsets === null ||
            highlightIndex.length === 0 ||
            !isFiniteNumber(sourceWithOffsets.chunk_start) ||
            !isFiniteNumber(sourceWithOffsets.chunk_end)
        ) {
            return
        }

        const targetPage = resolveBestPageForOffsets(
            sourceWithOffsets.chunk_start,
            sourceWithOffsets.chunk_end,
            highlightIndex
        )
        setActivePage(targetPage)
    }, [activeSource, highlightIndex])

    const activeSourceWithOffsets = activeSource as MessageSourceWithOffsets | null
    const activeChunkStart =
        activeSourceWithOffsets !== null &&
        isFiniteNumber(activeSourceWithOffsets.chunk_start)
            ? activeSourceWithOffsets.chunk_start
            : null
    const activeChunkEnd =
        activeSourceWithOffsets !== null &&
        isFiniteNumber(activeSourceWithOffsets.chunk_end)
            ? activeSourceWithOffsets.chunk_end
            : null

    const activePageIndex = highlightIndex[activePage - 1] ?? null
    const activePageChunkLocalStart =
        activeChunkStart !== null && activePageIndex !== null
            ? Math.max(0, activeChunkStart - activePageIndex.pageStart)
            : null
    const activePageChunkLocalEnd =
        activeChunkEnd !== null && activePageIndex !== null
            ? Math.max(
                  0,
                  Math.min(activePageIndex.pageEnd, activeChunkEnd) -
                      activePageIndex.pageStart
              )
            : null

    const renderHighlightedText = ({
        str,
        itemIndex
    }: {
        str: string
        itemIndex: number
    }) => {
        if (
            activePageIndex === null ||
            activePageChunkLocalStart === null ||
            activePageChunkLocalEnd === null
        ) {
            return escapeHtml(str)
        }

        const itemRange = activePageIndex.itemRanges[itemIndex] ?? null
        if (itemRange === null) {
            return escapeHtml(str)
        }

        const overlaps =
            itemRange.end > activePageChunkLocalStart &&
            itemRange.start < activePageChunkLocalEnd

        if (!overlaps) {
            return escapeHtml(str)
        }

        return `<mark style=\"background-color:#fde68a;padding:0 1px;border-radius:2px;\">${escapeHtml(str)}</mark>`
    }

    if (!isOpen) {
        return null
    }

    return createPortal(
        <div className={"fixed inset-0 z-[120] flex items-center justify-center p-4"}>
            <div
                className={"absolute inset-0 bg-black/45 backdrop-blur-sm"}
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-modal="true"
                className={"relative z-10 w-full max-w-7xl h-[88vh] bg-white rounded-xl border border-tremor-border shadow-2xl flex flex-col overflow-hidden"}>
                <div className={"flex items-center justify-between px-4 py-3 border-b border-tremor-border bg-tremor-background-muted"}>
                    <div>
                        <h4 className={"text-sm font-semibold text-tremor-content-emphasis"}>
                            Relevante Abschnitte im Dokument
                        </h4>
                        <p className={"text-xs text-tremor-content"}>
                            {documentTitle}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={"px-3 py-1.5 text-sm rounded border border-tremor-border bg-white text-tremor-content-emphasis hover:bg-tremor-background-muted"}>
                        Schliessen
                    </button>
                </div>

                <div className={"flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1.6fr_2fr]"}>
                    <div className={"border-b lg:border-b-0 lg:border-r border-tremor-border p-4 overflow-y-auto bg-primary-50/40 lg:min-w-[420px]"}>
                        <p className={"text-sm font-semibold text-primary-700 mb-3"}>
                            Gefundene Abschnitte
                        </p>
                        <div className={"space-y-2"}>
                            {sources.length === 0 && (
                                <p className={"text-sm text-tremor-content"}>
                                    Keine Abschnitte verfügbar.
                                </p>
                            )}
                            {sources.map((source, index) => {
                                const isActive = index === activeSourceIndex
                                return (
                                    <button
                                        key={getSourceKey(source, index)}
                                        type="button"
                                        onClick={() => onActiveSourceIndexChange(index)}
                                        className={`w-full text-left text-[15px] whitespace-pre-wrap rounded p-3 border transition-colors text-tremor-content-emphasis ${
                                            isActive
                                                ? "bg-amber-100/80 border-amber-300"
                                                : "bg-white border-primary-100 hover:bg-primary-50"
                                        }`}>
                                        <div className={"text-sm text-tremor-content mb-2"}>
                                            Abschnitt {index + 1} · Ähnlichkeit: {(source.similarity * 100).toFixed(1)}%
                                        </div>
                                        <div className={"text-tremor-content-strong"}>
                                            {source.chunk_text}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                        <p className={"text-sm text-tremor-content mt-4"}>
                            Klicke einen Abschnitt, um die Markierung im PDF zu setzen.
                        </p>
                    </div>

                    <div className={"min-h-0 bg-white"}>
                        {isLoading && (
                            <div className={"h-full flex items-center justify-center text-sm text-tremor-content"}>
                                Dokument wird geladen...
                            </div>
                        )}

                        {!isLoading && error !== null && (
                            <div className={"h-full flex items-center justify-center px-6 text-sm text-danger-900"}>
                                {error}
                            </div>
                        )}

                        {!isLoading &&
                            error === null &&
                            documentUrl !== null &&
                            activeSource !== null && (
                                <div className={"h-full flex flex-col"}>
                                    <div className={"px-3 py-2 border-b border-tremor-border bg-tremor-background-muted text-xs text-tremor-content flex items-center justify-between"}>
                                        <div>
                                            Seite {activePage} von {pageCount}
                                        </div>
                                        <div className={"flex items-center gap-2"}>
                                            <button
                                                type="button"
                                                className={"px-2 py-1 rounded border border-tremor-border bg-white disabled:opacity-50"}
                                                disabled={activePage <= 1}
                                                onClick={() =>
                                                    setActivePage((prev) =>
                                                        Math.max(1, prev - 1)
                                                    )
                                                }>
                                                Zurück
                                            </button>
                                            <button
                                                type="button"
                                                className={"px-2 py-1 rounded border border-tremor-border bg-white disabled:opacity-50"}
                                                disabled={activePage >= pageCount}
                                                onClick={() =>
                                                    setActivePage((prev) =>
                                                        Math.min(pageCount, prev + 1)
                                                    )
                                                }>
                                                Weiter
                                            </button>
                                        </div>
                                    </div>
                                    <div className={"flex-1 overflow-auto bg-slate-100 p-3"}>
                                        <Document
                                            file={documentUrl}
                                            loading={
                                                <div className={"text-sm text-tremor-content"}>
                                                    Dokument wird geladen...
                                                </div>
                                            }
                                            error={
                                                <div className={"text-sm text-danger-900"}>
                                                    PDF konnte nicht geladen werden.
                                                </div>
                                            }>
                                            <Page
                                                pageNumber={activePage}
                                                width={900}
                                                renderAnnotationLayer={true}
                                                renderTextLayer={true}
                                                customTextRenderer={renderHighlightedText}
                                            />
                                        </Document>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default SourceDocumentModal
